<script setup lang="ts">
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import { useResizeObserver } from '@vueuse/core';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { guestCopyKey } from '@/domain/reservations/reservation-guide';
  import type { TGuestPreviewFocus } from '@/domain/reservations/reservation-guide';
  import { groupGuestTimes } from '@/domain/reservations/reservation-guest-preview';
  import type {
    IGuestPreviewDay,
    IGuestPreviewModel,
    ISlotPeriod,
  } from '@/domain/reservations/reservation-guest-preview';
  import { shortWeekdaysMonFirst } from '@/utils/intl-weekdays.utils';

  /**
   * Replica of the diner's booking front door (Storefront V2 APP-BOOK-1),
   * computed from the guide's draft (D8). Register exception (§9.3): the
   * `reservations.guide.guest.*` copy mirrors the diner app's tú wording
   * verbatim, with `.voseo` twins for AR/UY, because this is what the guest
   * reads, not the owner. It keeps neutral admin tokens (a schematic, not the
   * store's colours). Nothing inside is an interactive control: the whole
   * frame is aria-hidden and inert.
   */
  const props = defineProps<{
    model: IGuestPreviewModel;
    focus: TGuestPreviewFocus;
    restaurantName: string;
    voseo: boolean;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const SHOWN_TIMES = 6;
  const MOCK_PARTY = 2;
  const MOCK_TIME_INDEX = 2;
  /** Party cells before «Más», as the storefront's 1–8 grid. */
  const PARTY_GRID_MAX = 8;
  const PERIOD_KEYS: Record<ISlotPeriod, string> = {
    midday: 'reservations.guide.guest.periodMidday',
    afternoon: 'reservations.guide.guest.periodAfternoon',
    night: 'reservations.guide.guest.periodNight',
  };

  const locale = computed(() => proxy.$i18n.locale);
  function capitalize(value: string): string {
    return value.charAt(0).toLocaleUpperCase(locale.value) + value.slice(1);
  }
  const weekdayNames = computed(() =>
    shortWeekdaysMonFirst(locale.value).map((name) =>
      capitalize(name.replace(/\.$/, ''))
    )
  );
  /** «Hoy», «Mañana», then the short weekday, as the storefront's day chips. */
  function dayLabel(day: IGuestPreviewDay, index: number): string {
    if (index === 0) return tr('reservations.guide.guest.today');
    if (index === 1) return tr('reservations.guide.guest.tomorrow');
    return weekdayNames.value[(day.weekday + 6) % 7];
  }
  /** «Jue, 1 oct»: the short date of the mail line and the continue bar. */
  function shortDate(date: string): string {
    return capitalize(
      new Intl.DateTimeFormat(locale.value, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      }).format(new Date(`${date}T00:00:00Z`))
    );
  }

  const showOff = computed(() => props.focus === 'off');
  const showParty = computed(
    () =>
      props.focus === 'party' ||
      props.focus === 'contact' ||
      props.focus === 'live'
  );
  const showDays = computed(
    () => props.focus === 'time' || props.focus === 'live'
  );
  const showTimes = computed(() => props.focus === 'time');
  const showOverflow = computed(
    () => props.focus === 'party' || props.focus === 'contact'
  );
  const showMail = computed(() => props.focus === 'contact');

  const partyCells = computed(() =>
    props.model.partyChips.filter((party) => party <= PARTY_GRID_MAX)
  );
  const showMoreParty = computed(() => props.model.maxParty > PARTY_GRID_MAX);
  // Mock selections: party 2 (or the last cell), the sample day (whose times
  // the replica shows) and the third shown time of the first time group.
  const selectedParty = computed(() => {
    const cells = partyCells.value;
    if (cells.includes(MOCK_PARTY)) return MOCK_PARTY;
    return cells.length ? cells[cells.length - 1] : 0;
  });
  const selectedDay = computed(() => props.model.sampleDay);

  const dayStrip = ref<HTMLElement | null>(null);
  const dayRow = ref<HTMLElement | null>(null);
  /**
   * The storefront's day chips run past the screen edge and the diner scrolls
   * them, so the replica's strip shows its row scrolled just enough for the
   * marked day (without one, the first day) to be whole. Measured from the
   * row's start every time, so a narrower frame never leaves it over-scrolled
   * once it widens again. Only the strip scrolls, never the page (no
   * scrollIntoView); a hidden strip measures 0 and waits for the observer.
   */
  function revealMarkedDay(): void {
    const strip = dayStrip.value;
    const cell =
      strip?.querySelector<HTMLElement>('.day-cell.selected') ??
      strip?.querySelector<HTMLElement>('.day-cell');
    if (!strip || !cell || !strip.clientWidth) return;
    strip.scrollLeft = 0;
    const style = getComputedStyle(strip);
    const box = strip.getBoundingClientRect();
    const start = box.left + parseFloat(style.paddingLeft);
    const end = box.right - parseFloat(style.paddingRight);
    const { left, right } = cell.getBoundingClientRect();
    // A right-to-left row starts at the right and scrolls to negative values.
    if (right > end) strip.scrollLeft = Math.ceil(right - end);
    else if (left < start) strip.scrollLeft = -Math.ceil(start - left);
  }
  // The strip resizes with the frame; the row with its labels (language,
  // fonts). The marked day moves with the draft and with the date.
  useResizeObserver([dayStrip, dayRow], revealMarkedDay);
  watch(
    [() => selectedDay.value?.date, () => props.model.days[0]?.date],
    revealMarkedDay,
    { flush: 'post' }
  );

  const timeGroups = computed(() =>
    groupGuestTimes(props.model.shifts).map((group) => ({
      key: `${group.shift ?? ''}|${group.period ?? ''}`,
      heading: group.showHeading
        ? group.shift ?? (group.period ? tr(PERIOD_KEYS[group.period]) : '')
        : '',
      shown: group.times.slice(0, SHOWN_TIMES),
      hidden: Math.max(0, group.times.length - SHOWN_TIMES),
      last: group.times[group.times.length - 1],
    }))
  );
  const selectedTime = computed(() => {
    const first = timeGroups.value[0];
    if (!first) return null;
    return first.shown[Math.min(MOCK_TIME_INDEX, first.shown.length - 1)];
  });
  /** «2 personas · Hoy 21:30», the storefront's sticky bar. */
  const continueSummary = computed(() => {
    const day = selectedDay.value;
    const time = selectedTime.value;
    if (!day || !time) return '';
    const people = String(
      proxy.$tc('reservations.guide.guest.people', selectedParty.value, {
        n: selectedParty.value,
      })
    );
    const days = props.model.days;
    const dayText =
      days[0]?.date === day.date
        ? tr('reservations.guide.guest.today')
        : days[1]?.date === day.date
        ? tr('reservations.guide.guest.tomorrow')
        : shortDate(day.date);
    return `${people} · ${dayText} ${time}`;
  });
  const frontSubtitle = computed(() =>
    tr(guestCopyKey('frontSubtitle', props.voseo))
  );
  const overflowText = computed(() =>
    tr(guestCopyKey('overflowText', props.voseo))
  );
  const offText = computed(() => tr(guestCopyKey('offText', props.voseo)));
  const mailTo = computed(() =>
    tr('reservations.guide.preview.mailTo', {
      email:
        props.model.contact.email ||
        tr('reservations.guide.preview.mailNoEmail'),
    })
  );
  const mailLine = computed(() => {
    const mail = props.model.mail;
    if (!mail) return '';
    return tr('reservations.guide.preview.mailLine', {
      date: shortDate(mail.date),
      time: mail.time,
      name: tr('reservations.guide.preview.mailGuest'),
    });
  });
</script>

<template>
  <div class="guest-screen" aria-hidden="true">
    <div class="screen-cover">
      <PosUiIcon name="arrow-left" :size="16" />
      <span class="cover-name">{{ restaurantName }}</span>
    </div>
    <div v-if="showOff" class="off-block">
      <p class="block-title">{{ $t('reservations.guide.guest.offTitle') }}</p>
      <p class="block-text">{{ offText }}</p>
      <div class="contact-actions">
        <span class="contact-action" :class="{ dimmed: !model.contact.call }">
          <PosUiIcon name="phone-outline" :size="16" />{{
            $t('reservations.guide.guest.call')
          }}
        </span>
        <span
          class="contact-action"
          :class="{ dimmed: !model.contact.whatsapp }"
        >
          <PosUiIcon name="whatsapp" :size="16" />{{
            $t('reservations.guide.guest.whatsapp')
          }}
        </span>
      </div>
    </div>
    <template v-else>
      <div class="screen-head">
        <p class="screen-title">
          {{ $t('reservations.guide.guest.frontTitle') }}
        </p>
        <p class="screen-subtitle">{{ frontSubtitle }}</p>
      </div>
      <section
        v-if="showParty"
        class="screen-section"
        :class="{ dimmed: focus === 'contact' }"
      >
        <p class="section-title">
          {{ $t('reservations.guide.guest.partyTitle') }}
        </p>
        <p v-if="model.bookable === 0" class="section-empty">
          {{ $t('reservations.guide.guest.noTableAny') }}
        </p>
        <template v-else>
          <div class="party-grid">
            <span
              v-for="party in partyCells"
              :key="party"
              class="screen-chip"
              :class="{ selected: party === selectedParty }"
              >{{ party }}</span
            >
            <span v-if="showMoreParty" class="screen-chip">{{
              $t('reservations.guide.guest.frontMore')
            }}</span>
          </div>
          <p class="big-group">
            {{
              $t('reservations.guide.guest.bigGroup', { max: model.maxParty })
            }}
          </p>
        </template>
      </section>
      <section v-if="showDays" class="screen-section">
        <p class="section-title">
          {{ $t('reservations.guide.guest.dateTitle') }}
        </p>
        <div ref="dayStrip" class="day-strip">
          <div ref="dayRow" class="day-row">
            <span
              v-for="(day, index) in model.days"
              :key="day.date"
              class="day-cell"
              :class="{
                selected: !!selectedDay && day.date === selectedDay.date,
                closed: !day.open,
              }"
            >
              <span class="day-name">{{ dayLabel(day, index) }}</span>
              <span class="day-number">{{ day.dayOfMonth }}</span>
            </span>
          </div>
        </div>
        <span class="screen-chip other-date">
          <PosUiIcon name="calendar-month-outline" :size="14" />{{
            $t('reservations.guide.guest.otherDate')
          }}
        </span>
      </section>
      <section v-if="showTimes" class="screen-section">
        <p class="section-title">
          {{ $t('reservations.guide.guest.timeTitle') }}
        </p>
        <p v-if="!timeGroups.length" class="section-empty">
          {{ $t('reservations.guide.guest.noTimes') }}
        </p>
        <div v-for="group in timeGroups" :key="group.key" class="shift-block">
          <p v-if="group.heading" class="shift-name">{{ group.heading }}</p>
          <div class="chip-row">
            <span
              v-for="time in group.shown"
              :key="time"
              class="screen-chip"
              :class="{ selected: time === selectedTime }"
              >{{ time }}</span
            >
          </div>
          <p v-if="group.hidden > 0" class="more-times">
            {{
              $t('reservations.guide.preview.moreTimes', {
                count: group.hidden,
                last: group.last,
              })
            }}
          </p>
        </div>
      </section>
      <div v-if="showTimes && continueSummary" class="screen-continue">
        <span class="continue-chip">{{
          $t('reservations.guide.guest.continue')
        }}</span>
        <span class="continue-summary">{{ continueSummary }}</span>
      </div>
      <div v-if="showOverflow" class="overflow-card">
        <p class="block-title">
          {{
            $t('reservations.guide.guest.overflowTitle', {
              max: model.maxParty,
            })
          }}
        </p>
        <p class="block-text">{{ overflowText }}</p>
        <div class="contact-actions">
          <span class="contact-action" :class="{ dimmed: !model.contact.call }">
            <PosUiIcon name="phone-outline" :size="16" />{{
              $t('reservations.guide.guest.call')
            }}
          </span>
          <span
            class="contact-action"
            :class="{ dimmed: !model.contact.whatsapp }"
          >
            <PosUiIcon name="whatsapp" :size="16" />{{
              $t('reservations.guide.guest.whatsapp')
            }}
          </span>
        </div>
      </div>
      <div v-if="showMail" class="mail-card">
        <p class="mail-to">{{ mailTo }}</p>
        <p class="mail-subject">
          {{ $t('reservations.guide.preview.mailSubject') }}
        </p>
        <p v-if="mailLine" class="mail-line">{{ mailLine }}</p>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  // A quiet phone frame: nothing inside reacts to the pointer. Spacing uses
  // margins and grid gaps only (old POS Chrome has no flex gap).
  .guest-screen {
    @apply tw-pointer-events-none tw-mx-auto tw-w-full tw-max-w-[20rem] tw-select-none tw-space-y-3 tw-rounded-[1.5rem] tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-p-4 tw-text-neutral-900;
    p {
      @apply tw-m-0;
    }
    // The storefront's compact header.
    .screen-cover {
      @apply tw-flex tw-items-center tw-space-x-2 tw-rounded-xl tw-bg-neutral-900 tw-px-3 tw-py-2 tw-text-on-inverse;
      .pos-ui-icon {
        @apply tw-flex-none;
      }
    }
    .cover-name {
      @apply tw-min-w-0 tw-truncate tw-text-sm tw-font-semibold;
    }
    .screen-head {
      @apply tw-space-y-0.5;
    }
    .screen-title {
      @apply tw-text-base tw-font-semibold;
    }
    .screen-subtitle {
      @apply tw-text-xs tw-text-neutral-500;
    }
    .screen-section {
      @apply tw-space-y-2;
      &.dimmed {
        @apply tw-opacity-50;
      }
    }
    .section-title {
      @apply tw-text-sm tw-font-semibold;
    }
    .section-empty {
      @apply tw-rounded-lg tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-xs tw-text-amber-800;
    }
    .party-grid {
      @apply tw-grid tw-grid-cols-5 tw-gap-1.5;
    }
    .chip-row {
      @apply tw-grid tw-grid-cols-4 tw-gap-1.5;
    }
    .screen-chip {
      @apply tw-flex tw-min-h-8 tw-min-w-0 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-px-1 tw-text-xs tw-font-medium tw-tabular-nums tw-text-neutral-700;
      &.selected {
        @apply tw-border-neutral-900 tw-bg-neutral-900 tw-text-on-inverse;
      }
    }
    // The quiet line that opens the big-group help on the storefront.
    .big-group {
      @apply tw-text-xs tw-text-neutral-500 tw-underline;
    }
    // The storefront's day chips: one row of equal chips that runs past the
    // screen edge, so every label shows whole and the last chip is cut. The
    // strip bleeds to the frame's edge like the storefront's row to the
    // screen's, and clips instead of scrolling (the frame is inert).
    .day-strip {
      @apply -tw-mx-4 tw-overflow-hidden tw-px-4;
    }
    // A max-content grid of 1fr columns: every chip as wide as the widest
    // label, never narrower than 3rem.
    .day-row {
      @apply tw-grid tw-w-max tw-auto-cols-[minmax(3rem,1fr)] tw-grid-flow-col tw-gap-1.5;
    }
    .day-cell {
      @apply tw-flex tw-min-h-12 tw-flex-col tw-items-center tw-justify-center tw-space-y-0.5 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-px-1 tw-py-1 tw-text-center;
      .day-name {
        @apply tw-whitespace-nowrap tw-text-[10px] tw-text-neutral-500;
      }
      .day-number {
        @apply tw-text-xs tw-font-semibold tw-tabular-nums;
      }
      // The storefront's disabled day chip.
      &.closed {
        @apply tw-opacity-40;
      }
      &.selected {
        @apply tw-border-neutral-900 tw-bg-neutral-900 tw-text-on-inverse;
        .day-name {
          @apply tw-text-neutral-300;
        }
      }
    }
    .other-date {
      @apply tw-w-full;
      .pos-ui-icon {
        @apply tw-mr-1.5;
      }
    }
    .shift-block {
      @apply tw-space-y-1.5;
    }
    .shift-name {
      @apply tw-text-xs tw-font-medium tw-text-neutral-500;
    }
    .more-times {
      @apply tw-text-[11px] tw-text-neutral-500;
    }
    // The storefront's sticky bar: «Continuar», then the summary.
    .screen-continue {
      @apply tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-bg-neutral-900 tw-px-3 tw-py-2 tw-text-on-inverse;
    }
    .continue-chip {
      @apply tw-mr-2 tw-flex-none tw-rounded-full tw-bg-surface tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-neutral-900;
    }
    .continue-summary {
      @apply tw-min-w-0 tw-truncate tw-text-xs;
    }
    .overflow-card,
    .off-block {
      @apply tw-space-y-2 tw-rounded-xl tw-bg-canvas tw-p-3;
    }
    .block-title {
      @apply tw-text-sm tw-font-semibold;
    }
    .block-text {
      @apply tw-text-xs tw-leading-relaxed tw-text-neutral-600;
    }
    .contact-actions {
      @apply tw-grid tw-grid-cols-2 tw-gap-2;
    }
    .contact-action {
      @apply tw-flex tw-min-h-9 tw-min-w-0 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-surface tw-px-3 tw-text-xs tw-font-medium tw-text-neutral-800;
      .pos-ui-icon {
        @apply tw-mr-1.5;
      }
      &.dimmed {
        @apply tw-opacity-40;
      }
    }
    .mail-card {
      @apply tw-space-y-1 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-p-3;
    }
    .mail-to {
      @apply tw-truncate tw-text-[11px] tw-text-neutral-500;
    }
    .mail-subject {
      @apply tw-text-sm tw-font-semibold;
    }
    .mail-line {
      @apply tw-text-xs tw-text-neutral-600;
    }
  }
</style>
